// APP TEMPLATES CONTROL PANEL (M3 — docs/app-templates.md §7).
//
// platform_admin board over the template library: every template with its version,
// contract size and provenance; every managed INSTALL with its version and an
// "update available" drift badge; readiness drill-down against any project;
// install (gated server-side on readiness — 412 shows the per-element
// detail), upgrade (re-stamp to the library version, same gate) and
// uninstall. The nav item renders only for platform admins; every API call is
// enforced server-side regardless.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Package, RefreshCw, Trash2, ArrowUpCircle, CircleCheck, CircleX, CircleAlert, Pin, PinOff, KeyRound, History, PauseCircle, PlayCircle, Building2 } from "lucide-react";
import { templatesOverview, templateReadiness, installTemplate, uninstallTemplate, pinTemplateInstall, mintDeployCredential, revokeDeployCredential, assignTemplate, unassignTemplate, suspendTemplate, upgradeAllInstalls, deleteTemplate } from "@/services/templates";
import { fetchData } from "@/fetchapi/fetchapi";
import { listOrgs } from "@/services/orgs";
import { createProject } from "@/services/projects";

const READY_BADGE = {
  ready: { icon: CircleCheck, cls: "text-emerald-600", label: "ready" },
  partial: { icon: CircleAlert, cls: "text-amber-600", label: "partial" },
  not_ready: { icon: CircleX, cls: "text-red-600", label: "not ready" },
};

// Assignment lifecycle pill (spec §7 in-product status).
const STATUS_PILL = {
  assigned:  "text-slate-600 bg-slate-100 border-slate-200",
  preparing: "text-amber-700 bg-amber-50 border-amber-200",
  ready:     "text-emerald-700 bg-emerald-50 border-emerald-200",
  published: "text-indigo-700 bg-indigo-50 border-indigo-200",
  suspended: "text-red-700 bg-red-50 border-red-200",
};
function StatusPill({ status, tpl, org }) {
  return (
    <span
      data-testid={`tpl-status-${tpl}-${org}`}
      className={`inline-flex items-center rounded-full border px-2 py-[1px] text-[11px] leading-tight ${STATUS_PILL[status] || STATUS_PILL.assigned}`}
    >
      {status}
    </span>
  );
}

function ReadinessDetail({ readiness }) {
  if (!readiness?.elements?.length) return null;
  const missing = readiness.elements.filter((e) => e.required && !e.satisfied);
  return (
    <div className="mt-2 border border-page-divider rounded-[5px] overflow-hidden" data-testid="tpl-readiness-detail">
      <div className="px-3 py-1.5 text-xs text-page-muted bg-page-bg">
        {readiness.satisfied}/{readiness.required} required elements ready
        {missing.length > 0 && " — the target's data model must provide the rest before install"}
      </div>
      <div className="max-h-52 overflow-y-auto">
        {readiness.elements.map((el) => (
          <div key={`${el.entity}.${el.canonical_name}`} className="flex items-center gap-2 px-3 py-1.5 text-xs border-t border-page-divider">
            <span className={el.satisfied ? "text-emerald-600" : "text-red-600"}>{el.satisfied ? "●" : "○"}</span>
            <span className="font-medium">{el.canonical_name}</span>
            {!el.required && <span className="text-page-muted">(optional)</span>}
            {!el.satisfied && (
              <span className="text-page-muted ml-auto">
                {!el.exists ? "missing element" : !el.type_ok ? "type mismatch" : !el.bound ? "not bound to a target" : "no source mapping"}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Per-template install workbench — ORGANIZATION FIRST (this is a cross-org
// mechanism: templates are assigned per organization): pick the org, then a
// project inside it — or create a NEW project in that org as the install
// target (spec §5.2 step 1) — preview readiness, install only when the
// server will accept it.
function InstallPanel({ tpl, projects, orgs, onDone, onProjectCreated }) {
  const [org, setOrg] = useState("");
  const [project, setProject] = useState(""); // slug | "__new__"
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  // Version to stamp: latest release by default; older releases = rollback.
  const [version, setVersion] = useState("");
  const [readiness, setReadiness] = useState(null);
  const [checking, setChecking] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [error, setError] = useState(null);

  const check = useCallback(async (slug) => {
    setProject(slug); setReadiness(null); setError(null);
    if (!slug || slug === "__new__") return;
    setChecking(true);
    try {
      setReadiness(await templateReadiness(tpl.template, slug));
    } catch (e) { setError(e?.message || "readiness check failed"); }
    setChecking(false);
  }, [tpl.template]);

  const createTarget = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true); setError(null);
    try {
      const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 48);
      const row = await createProject({ name, slug, org_slug: org, description: `Created as install target for app template '${tpl.template}'` });
      onProjectCreated(row);
      setNewName("");
      await check(row.slug);
    } catch (e) { setError(e?.message || "project creation failed"); }
    setCreating(false);
  };

  const install = async () => {
    setInstalling(true); setError(null);
    try {
      await installTemplate(tpl.template, project, version || undefined);
      setReadiness(null); setProject(""); setOrg(""); setVersion("");
      onDone();
    } catch (e) {
      // 412 carries the readiness detail — show exactly what is missing.
      if (e?.readiness) setReadiness({ ...e.readiness });
      setError(e?.message || "install failed");
    }
    setInstalling(false);
  };

  const installedSlugs = new Set(tpl.installs.map((i) => i.project_slug));
  // Target organizations: every org except the one holding the library itself
  // (organization.kind is a label for exactly this, never a permission).
  const targetOrgs = orgs.filter((o) => o.slug !== "app_templates" && o.kind !== "template_hub");
  const candidates = projects.filter((p) => p.org_slug === org && p.slug !== "app_templates" && !installedSlugs.has(p.slug));
  const projectChosen = project && project !== "__new__";

  return (
    <div className="mt-3 pt-3 border-t border-page-divider" data-testid={`tpl-install-panel-${tpl.template}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <select
          value={org}
          onChange={(e) => { setOrg(e.target.value); setProject(""); setReadiness(null); setError(null); }}
          data-testid={`tpl-install-org-${tpl.template}`}
          className="text-sm rounded-[5px] border border-page-divider bg-card-bg px-2 py-1.5 min-w-[190px]"
        >
          <option value="">Organization…</option>
          {targetOrgs.map((o) => <option key={o.slug} value={o.slug}>{o.name || o.slug}</option>)}
        </select>
        {org && (
          <select
            value={project}
            onChange={(e) => check(e.target.value)}
            data-testid={`tpl-install-project-${tpl.template}`}
            className="text-sm rounded-[5px] border border-page-divider bg-card-bg px-2 py-1.5 min-w-[200px]"
          >
            <option value="">Project in {org}…</option>
            {candidates.map((p) => <option key={p.slug} value={p.slug}>{p.slug}</option>)}
            <option value="__new__">＋ New project…</option>
          </select>
        )}
        {project === "__new__" && (
          <>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && newName.trim() && !creating) createTarget(); }}
              placeholder={`New project in ${org}`}
              data-testid={`tpl-newproj-name-${tpl.template}`}
              className="text-sm rounded-[5px] border border-page-divider bg-card-bg px-2 py-1.5 min-w-[200px]"
            />
            <button
              type="button"
              disabled={creating || !newName.trim()}
              onClick={createTarget}
              data-testid={`tpl-newproj-create-${tpl.template}`}
              className="flex items-center gap-1.5 h-8 px-3 rounded-[5px] border border-page-divider text-xs font-medium cursor-pointer hover:bg-page-bg disabled:opacity-40"
            >
              {creating ? <Loader2 size={13} className="animate-spin" /> : "Create"}
            </button>
          </>
        )}
        {tpl.releases?.length > 0 && projectChosen && (
          <select
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            data-testid={`tpl-install-version-${tpl.template}`}
            title="Version to install — older releases roll back byte-identically"
            className="text-sm rounded-[5px] border border-page-divider bg-card-bg px-2 py-1.5"
          >
            <option value="">latest (v{tpl.version})</option>
            {[...tpl.releases].reverse().map((v) => <option key={v} value={v}>v{v}</option>)}
          </select>
        )}
        {checking && <Loader2 size={15} className="animate-spin text-page-muted" />}
        {readiness && (() => { const b = READY_BADGE[readiness.status] || READY_BADGE.not_ready; const I = b.icon; return (
          <span className={`inline-flex items-center gap-1 text-xs ${b.cls}`} data-testid={`tpl-readiness-badge-${tpl.template}`}>
            <I size={14} /> {b.label}
          </span>
        ); })()}
        {org && (
          <button
            type="button"
            title="Assign this organization without publishing yet — it appears in the matrix as 'assigned' and prepares its data model"
            disabled={installing}
            onClick={async () => {
              setError(null);
              try { await assignTemplate(tpl.template, org, projectChosen ? project : undefined); onDone(); }
              catch (e) { setError(e?.message || "assign failed"); }
            }}
            data-testid={`tpl-assign-btn-${tpl.template}`}
            className="flex items-center gap-1.5 h-8 px-3 rounded-[5px] border border-page-divider text-xs font-medium cursor-pointer hover:bg-page-bg"
          >
            Assign only
          </button>
        )}
        <button
          type="button"
          disabled={!projectChosen || installing || (readiness && readiness.status !== "ready")}
          onClick={install}
          data-testid={`tpl-install-btn-${tpl.template}`}
          className="ml-auto flex items-center gap-1.5 h-8 px-3 rounded-[5px] bg-nav-active text-white text-xs font-medium cursor-pointer hover:bg-nav-active-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {installing ? <Loader2 size={13} className="animate-spin" /> : <Package size={13} />} Install
        </button>
      </div>
      {error && <div className="mt-2 text-xs text-red-700">{error}</div>}
      {readiness && readiness.status !== "ready" && <ReadinessDetail readiness={readiness} />}
    </div>
  );
}

export default function Templates() {
  const [templates, setTemplates] = useState(null);
  const [projects, setProjects] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [error, setError] = useState(null);
  // Per-install readiness snapshots: `${tpl}:${project}` -> readiness result.
  const [rowReadiness, setRowReadiness] = useState({});
  const [busy, setBusy] = useState(null); // `${tpl}:${project}` while acting
  const [mintedCred, setMintedCred] = useState(null); // one-time secret display
  const [detailRow, setDetailRow] = useState(null);   // matrix row with readiness drill-down open
  const [confirmDelete, setConfirmDelete] = useState(null); // template slug awaiting the second click

  const load = useCallback(async () => {
    try {
      const [tpls, projRes, orgRows] = await Promise.all([
        templatesOverview(),
        fetchData("projects"),
        listOrgs().catch(() => []),
      ]);
      setTemplates(tpls);
      const projRows = projRes.projects || [];
      setProjects(projRows);
      // Orgs: the full list when the caller may read it; otherwise derived
      // from the projects they can see.
      const derived = [...new Set(projRows.map((p) => p.org_slug).filter(Boolean))].map((slug) => ({ slug, name: slug }));
      setOrgs(orgRows.length ? orgRows : derived);
      setError(null);
    } catch (e) { setError(e?.message || "failed to load templates"); setTemplates([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (key, fn) => {
    setBusy(key); setError(null);
    try { await fn(); await load(); } catch (e) { setError(e?.message || "action failed"); }
    setBusy(null);
  };

  return (
    <div className="pt-[30px] pb-24 md:pb-[30px] px-4 md:px-[60px] bg-page-bg min-h-full flex flex-col gap-5" data-testid="templates-page">
      <div className="flex flex-col gap-1.5">
        <h1 className="text-[32px] font-medium leading-tight text-page-title">App Templates</h1>
        <p className="text-sm font-normal text-page-muted">
          Publish once, install per organization project. Installs are gated on the target's data-model
          readiness and stay centrally managed — org users can open a managed app but never change it.
        </p>
      </div>

      {error && <div className="bg-red-50 text-red-800 px-4 py-2 rounded-[5px] text-sm" data-testid="tpl-error">{error}</div>}
      {mintedCred && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 px-4 py-3 rounded-[5px] text-sm" data-testid="tpl-cred-banner">
          <div className="font-medium mb-1">Deployment credential minted — copy the secret now, it is shown only once.</div>
          <div className="font-mono text-xs">client_id: {mintedCred.client_id}</div>
          <div className="font-mono text-xs break-all">client_secret: {mintedCred.client_secret}</div>
          <button type="button" onClick={() => setMintedCred(null)} className="mt-2 text-xs underline cursor-pointer bg-transparent border-0">Dismiss</button>
        </div>
      )}
      {templates === null && <div className="text-page-muted text-sm"><Loader2 className="inline animate-spin mr-2" size={16} />Loading…</div>}
      {templates?.length === 0 && (
        <div className="text-sm text-page-muted bg-card-bg rounded-[8px] px-4 py-6">
          No templates in the library yet. Author the app as usual in any project — its governed data
          model becomes the template&apos;s contract — then use
          <span className="font-medium"> &quot;Publish as template&quot;</span> from the app&apos;s menu on the Apps page.
        </div>
      )}

      {templates?.map((tpl) => (
        <div key={tpl.template} className="bg-card-bg rounded-[8px] px-4 py-4" data-testid={`tpl-card-${tpl.template}`}>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <Package size={17} className="text-page-muted" />
            <span className="text-base font-medium text-page-title">{tpl.title || tpl.template}</span>
            <span className="text-xs text-page-muted font-mono">{tpl.template}</span>
            <span className="text-xs rounded-full border border-page-divider px-2 py-[2px]">v{tpl.version || "?"}</span>
            <span className="text-xs text-page-muted">
              contract: {tpl.contract_elements} element{tpl.contract_elements === 1 ? "" : "s"}
            </span>
            {tpl.releases?.length > 0 && (
              <span className="text-xs text-page-muted" data-testid={`tpl-releases-${tpl.template}`}>
                releases: {tpl.releases.map((v) => `v${v}`).join(", ")}
              </span>
            )}
            <span className="ml-auto inline-flex items-center gap-3">
              {tpl.source_project && (
                <a
                  href={`/p/${encodeURIComponent(tpl.source_project)}/b/main/apps/${encodeURIComponent(tpl.source_view)}`}
                  title="Open the source app this template was published from"
                  data-testid={`tpl-source-link-${tpl.template}`}
                  className="text-xs text-page-muted hover:text-nav-active hover:underline"
                >
                  from {tpl.source_project}/{tpl.source_view}
                </a>
              )}
              <button
                type="button"
                title={tpl.installs.length > 0
                  ? "Delete is blocked while organizations still run this template — uninstall them first"
                  : "Delete this template from the library (releases included)"}
                disabled={busy === `${tpl.template}:delete` || tpl.installs.length > 0}
                onClick={() => {
                  if (confirmDelete !== tpl.template) { setConfirmDelete(tpl.template); return; }
                  setConfirmDelete(null);
                  act(`${tpl.template}:delete`, () => deleteTemplate(tpl.template));
                }}
                data-testid={`tpl-delete-${tpl.template}`}
                className={`inline-flex items-center gap-1 text-xs cursor-pointer bg-transparent border-0 disabled:opacity-40 disabled:cursor-not-allowed ${confirmDelete === tpl.template ? "text-red-700 font-medium" : "text-red-600 hover:underline"}`}
              >
                <Trash2 size={13} /> {confirmDelete === tpl.template ? "Confirm delete" : "Delete"}
              </button>
            </span>
          </div>
          {tpl.actions?.length > 0 && (
            <details className="mt-2">
              <summary className="text-xs text-page-muted cursor-pointer inline-flex items-center gap-1"><History size={12} /> recent actions</summary>
              <div className="mt-1 text-xs text-page-muted space-y-0.5" data-testid={`tpl-actions-${tpl.template}`}>
                {tpl.actions.map((aRow, i) => (
                  <div key={i}>
                    {new Date(aRow.at).toLocaleString()} — <span className="font-medium">{aRow.action}</span>
                    {aRow.version ? ` v${aRow.version}` : ""}{aRow.project_slug ? ` · ${aRow.project_slug}` : ""} · {aRow.actor_email}
                  </div>
                ))}
              </div>
            </details>
          )}

          {tpl.organizations?.length > 0 && (
            <div className="overflow-x-auto">
            <table className="w-full text-sm mt-3">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-page-muted border-b border-page-divider">
                  <th className="py-1.5 font-medium">Organization</th>
                  <th className="py-1.5 font-medium">Project</th>
                  <th className="py-1.5 font-medium">Status</th>
                  <th className="py-1.5 font-medium">Data model</th>
                  <th className="py-1.5 font-medium">Version</th>
                  <th className="py-1.5 font-medium">Deployment</th>
                  <th className="py-1.5 font-medium">Last action</th>
                  <th className="py-1.5 font-medium" />
                </tr>
              </thead>
              <tbody>
                {tpl.organizations.map((row) => {
                  const inst = row.install;
                  const proj = row.project_slug;
                  const key = `${tpl.template}:${proj || row.org_slug}`;
                  const rd = rowReadiness[key] || row.readiness; // fresh check wins over stored
                  return (
                    <React.Fragment key={row.org_slug}>
                    <tr className="border-b border-page-divider last:border-0 align-top" data-testid={`tpl-org-row-${tpl.template}-${row.org_slug}`}>
                      <td className="py-2 font-medium"><span className="inline-flex items-center gap-1.5"><Building2 size={13} className="text-page-muted" />{row.org_slug}</span></td>
                      <td className="py-2">{proj || <span className="text-page-muted">—</span>}</td>
                      <td className="py-2"><StatusPill status={row.status} tpl={tpl.template} org={row.org_slug} /></td>
                      <td className="py-2">
                        {rd ? (() => { const b = READY_BADGE[rd.status] || READY_BADGE.not_ready; const I = b.icon; return (
                          <button
                            type="button"
                            title={`${rd.satisfied}/${rd.required} required elements ready${row.readiness_checked_at ? ` · checked ${new Date(row.readiness_checked_at).toLocaleString()}` : ""} — click for detail`}
                            onClick={() => setDetailRow(detailRow === key ? null : key)}
                            data-testid={`tpl-row-readiness-${tpl.template}-${row.org_slug}`}
                            className={`inline-flex items-center gap-1 text-xs cursor-pointer bg-transparent border-0 ${b.cls}`}
                          >
                            <I size={13} /> {rd.satisfied}/{rd.required}
                          </button>
                        ); })() : <span className="text-xs text-page-muted">{tpl.contract_elements === 0 ? "no contract" : "unchecked"}</span>}
                        {proj && (
                          <button
                            type="button"
                            title="Re-check this project's data model against the contract"
                            disabled={busy === key}
                            onClick={() => act(key, async () => {
                              const r = await templateReadiness(tpl.template, proj);
                              setRowReadiness((m) => ({ ...m, [key]: r }));
                            })}
                            data-testid={`tpl-row-check-${tpl.template}-${row.org_slug}`}
                            className="ml-2 inline-flex items-center gap-1 text-[11px] text-page-muted hover:text-page-text cursor-pointer bg-transparent border-0"
                          >
                            <RefreshCw size={11} /> check
                          </button>
                        )}
                      </td>
                      <td className="py-2 whitespace-nowrap">
                        {inst ? (<>
                          v{inst.installed_version || "?"}
                          {inst.pinned && (
                            <span className="ml-2 text-[11px] text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-2 py-[1px]" data-testid={`tpl-pinned-${tpl.template}-${inst.project_slug}`}>pinned</span>
                          )}
                          {inst.outdated && !inst.pinned && (
                            <span className="ml-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-[1px]" data-testid={`tpl-outdated-${tpl.template}-${inst.project_slug}`}>→ v{tpl.version}</span>
                          )}
                        </>) : <span className="text-page-muted">—</span>}
                      </td>
                      <td className="py-2">
                        {(inst?.credentials || []).filter((c) => !c.revoked_at).length === 0 && <span className="text-xs text-page-muted">—</span>}
                        {(inst?.credentials || []).filter((c) => !c.revoked_at).map((c) => (
                          <span key={c.client_id} className="mr-1 inline-flex items-center gap-1 text-[11px] text-page-muted border border-page-divider rounded-full px-2 py-[1px]" title={`Deployment credential${c.deployed_version ? ` — deployed v${c.deployed_version}` : " — credential minted, not deployed yet"}`}>
                            <KeyRound size={10} /> {c.client_id.slice(-6)}{c.deployed_version ? ` · v${c.deployed_version}` : ""}
                            <button type="button" title="Revoke" onClick={() => act(key, () => revokeDeployCredential(c.client_id))} data-testid={`tpl-cred-revoke-${c.client_id}`} className="cursor-pointer bg-transparent border-0 text-red-500 hover:text-red-700 p-0 leading-none">×</button>
                          </span>
                        ))}
                      </td>
                      <td className="py-2 text-xs text-page-muted whitespace-nowrap" data-testid={`tpl-last-action-${tpl.template}-${row.org_slug}`}>
                        {row.last_action ? `${row.last_action.action}${row.last_action.version ? ` v${row.last_action.version}` : ""} · ${new Date(row.last_action.at).toLocaleString()}` : "—"}
                      </td>
                      <td className="py-2 text-right whitespace-nowrap">
                        {inst && (<>
                          <button type="button" title={inst.pinned ? "Unpin — allow upgrades again" : "Pin to this version"} disabled={busy === key}
                            onClick={() => act(key, () => pinTemplateInstall(tpl.template, inst.project_slug, !inst.pinned))}
                            data-testid={`tpl-pin-${tpl.template}-${inst.project_slug}`}
                            className="inline-flex items-center gap-1 text-xs text-page-muted hover:text-page-text mr-3 cursor-pointer bg-transparent border-0">
                            {inst.pinned ? <PinOff size={13} /> : <Pin size={13} />} {inst.pinned ? "Unpin" : "Pin"}
                          </button>
                          <button type="button" title="Mint a deployment credential (secret shown once)" disabled={busy === key}
                            onClick={() => act(key, async () => { setMintedCred(await mintDeployCredential(tpl.template, inst.project_slug)); })}
                            data-testid={`tpl-mint-cred-${tpl.template}-${inst.project_slug}`}
                            className="inline-flex items-center gap-1 text-xs text-page-muted hover:text-page-text mr-3 cursor-pointer bg-transparent border-0">
                            <KeyRound size={13} /> Credential
                          </button>
                          {inst.outdated && !inst.pinned && (
                            <button type="button" title={`Re-stamp to v${tpl.version} (readiness-gated)`} disabled={busy === key}
                              onClick={() => act(key, () => installTemplate(tpl.template, inst.project_slug))}
                              data-testid={`tpl-upgrade-${tpl.template}-${inst.project_slug}`}
                              className="inline-flex items-center gap-1 text-xs text-nav-active hover:underline mr-3 cursor-pointer bg-transparent border-0">
                              <ArrowUpCircle size={13} /> Upgrade
                            </button>
                          )}
                          <button type="button" title="Suspend — unpublish from the project, keep the assignment" disabled={busy === key}
                            onClick={() => act(key, () => suspendTemplate(tpl.template, inst.project_slug))}
                            data-testid={`tpl-suspend-${tpl.template}-${inst.project_slug}`}
                            className="inline-flex items-center gap-1 text-xs text-page-muted hover:text-page-text mr-3 cursor-pointer bg-transparent border-0">
                            <PauseCircle size={13} /> Suspend
                          </button>
                          <button type="button" title="Uninstall from this project" disabled={busy === key}
                            onClick={() => act(key, () => uninstallTemplate(tpl.template, inst.project_slug))}
                            data-testid={`tpl-uninstall-${tpl.template}-${inst.project_slug}`}
                            className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline cursor-pointer bg-transparent border-0">
                            {busy === key ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Uninstall
                          </button>
                        </>)}
                        {!inst && row.status === "suspended" && proj && (
                          <button type="button" title="Resume — re-publish into the project (readiness-gated)" disabled={busy === key}
                            onClick={() => act(key, () => installTemplate(tpl.template, proj))}
                            data-testid={`tpl-resume-${tpl.template}-${row.org_slug}`}
                            className="inline-flex items-center gap-1 text-xs text-nav-active hover:underline mr-3 cursor-pointer bg-transparent border-0">
                            <PlayCircle size={13} /> Resume
                          </button>
                        )}
                        {!inst && (
                          <button type="button" title="Unassign this organization" disabled={busy === key}
                            onClick={() => act(key, () => unassignTemplate(tpl.template, row.org_slug))}
                            data-testid={`tpl-unassign-${tpl.template}-${row.org_slug}`}
                            className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline cursor-pointer bg-transparent border-0">
                            <Trash2 size={13} /> Unassign
                          </button>
                        )}
                      </td>
                    </tr>
                    {detailRow === key && rd?.elements && (
                      <tr><td colSpan={8}><ReadinessDetail readiness={rd} /></td></tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}

          {tpl.installs.some((i) => i.outdated && !i.pinned) && (
            <button
              type="button"
              disabled={busy === `${tpl.template}:*`}
              onClick={() => act(`${tpl.template}:*`, () => upgradeAllInstalls(tpl.template))}
              data-testid={`tpl-upgrade-all-${tpl.template}`}
              className="mt-2 inline-flex items-center gap-1.5 text-xs text-nav-active hover:underline cursor-pointer bg-transparent border-0"
              title="Upgrade every install to the latest release — pinned and not-ready projects are skipped"
            >
              <ArrowUpCircle size={13} /> Upgrade all (skips pinned / not ready)
            </button>
          )}

          <InstallPanel
            tpl={tpl}
            projects={projects}
            orgs={orgs}
            onDone={load}
            onProjectCreated={(row) => setProjects((rows) => [...rows, row])}
          />
        </div>
      ))}

      {templates !== null && (
        <button
          type="button"
          onClick={load}
          className="self-start flex items-center gap-1.5 text-xs text-page-muted hover:text-page-text cursor-pointer bg-transparent border-0"
        >
          <RefreshCw size={13} /> Refresh
        </button>
      )}
    </div>
  );
}
